export const PLAYER_BONUS_TYPES = {
  NONE: 'none',
  // Убран из выбора 29.10 (решение владельца): остался только в старых
  // записях, их числа — в LEGACY_PLAYER_BONUS_CONFIG (configs/playerBonusLegacy)
  SMALL: 'small',
  MEDIUM: 'medium',
  BIG: 'big',
  LARGE: 'large',
  GREAT: 'great',
  DIVINE: 'divine',
};

/**
 * Числа уровней бонуса. Экономика (стартовые монеты, доход монет, действия в
 * фазе Строительства) действует на любого игрока с бонусом:
 *  - coinsIncomeBonus — монеты к доходу каждый раунд, пока у игрока есть хотя
 *    бы один город (как базовый доход); у всех уровней одинаковая;
 *  - coinsIncomePerCityBonus — еще монеты к доходу за КАЖДЫЙ город, которым
 *    игрок владеет (заблокированы ли его кварталы, неважно); без городов
 *    прибавки нет (см. calcPlayerIncomeBreakdown); у «маленького» ее нет;
 *  - winPointsIncomePerCityBonus — так же очки победы, только у «божества»;
 *  - d6MinRoll — пол броска d6, только у бота, помеченного ботом для всех:
 *    явного бота рейтингового матча и бота локальной партии (см.
 *    getBotD6MinRoll). У «божества» пол 6 — кость урона всегда 0, кроме боя
 *    с ботами: против них он бросает обычные кости (см.
 *    checkIfAnyOpponentIsBot).
 *
 * Решения владельца 29.10. Сначала доход бонуса стал за каждый город, а не
 * фиксированным (так против ботов интереснее играть: каждый отбитый город
 * отнимает у бота часть бонуса). Затем — числа ниже: доход за город у каждого
 * уровня на 1 меньше, зато всем уровням поровну +1 к доходу, пока есть хотя бы
 * один город; стартовые монеты +2, кроме «огромного» (рубин) — у него −2.
 * Уровни по лигам ботов: MEDIUM — серебро, BIG — золото, LARGE — алмаз,
 * GREAT — рубин, DIVINE — божество (configs/visibleRankedBots).
 *
 * Прежние числа — в configs/playerBonusLegacy: «только за города»
 * (PER_CITY_ONLY_PLAYER_BONUS_CONFIG) и фиксированный доход до 29.10
 * (LEGACY_PLAYER_BONUS_CONFIG, там же winPointsIncomeBonus). Записи
 * проигрываются по числам, зафиксированным в самом игроке (bonusConfig), —
 * расчет дохода понимает любое сочетание полей.
 *
 * Поведенческие надбавки ботов нормируются по «великому» (см.
 * configs/botBonusBehavior): у «божества» доход больше, но надбавки те же.
 * Шкала надбавок привязана к уровню: правка чисел ниже поведения ботов не
 * меняет (см. getBotBonusIntensity).
 */
export const PLAYER_BONUS_CONFIG = {
  [PLAYER_BONUS_TYPES.NONE]: {
    coinsIncomeBonus: 0,
    coinsIncomePerCityBonus: 0,
    constructionActionsBonus: 0,
    startingCoinsBonus: 0,
  },
  [PLAYER_BONUS_TYPES.MEDIUM]: {
    coinsIncomeBonus: 1,
    coinsIncomePerCityBonus: 0,
    constructionActionsBonus: 1,
    startingCoinsBonus: 5,
  },
  [PLAYER_BONUS_TYPES.BIG]: {
    coinsIncomeBonus: 1,
    coinsIncomePerCityBonus: 1,
    constructionActionsBonus: 2,
    startingCoinsBonus: 8,
  },
  [PLAYER_BONUS_TYPES.LARGE]: {
    coinsIncomeBonus: 1,
    coinsIncomePerCityBonus: 2,
    constructionActionsBonus: 3,
    startingCoinsBonus: 10,
  },
  [PLAYER_BONUS_TYPES.GREAT]: {
    coinsIncomeBonus: 1,
    coinsIncomePerCityBonus: 3,
    constructionActionsBonus: 3,
    startingCoinsBonus: 13,
  },
  [PLAYER_BONUS_TYPES.DIVINE]: {
    coinsIncomeBonus: 1,
    coinsIncomePerCityBonus: 4,
    constructionActionsBonus: 3,
    startingCoinsBonus: 17,
    winPointsIncomePerCityBonus: 1,
    d6MinRoll: 6,
  },
};

// Подписи уровней. 29.10 «маленький» убран из выбора, а подписи остальных
// уровней сдвинуты на ступень вниз (решение владельца): MEDIUM теперь
// «маленький», BIG — «средний», LARGE — «большой», GREAT — «огромный»;
// «божество» осталось. Подпись SMALL — для старых записей. В комментариях кода,
// написанных до 29.10, уровни названы прежними подписями: «большой» там — BIG
// (золото), «огромный» — LARGE, «великий» — GREAT
export const PLAYER_BONUS_LABELS = {
  [PLAYER_BONUS_TYPES.NONE]: 'нет',
  [PLAYER_BONUS_TYPES.SMALL]: 'маленький',
  [PLAYER_BONUS_TYPES.MEDIUM]: 'маленький',
  [PLAYER_BONUS_TYPES.BIG]: 'средний',
  [PLAYER_BONUS_TYPES.LARGE]: 'большой',
  [PLAYER_BONUS_TYPES.GREAT]: 'огромный',
  [PLAYER_BONUS_TYPES.DIVINE]: 'божество',
};

export const getPlayerBonusConfig = (bonusType) =>
  PLAYER_BONUS_CONFIG[bonusType] || PLAYER_BONUS_CONFIG[PLAYER_BONUS_TYPES.NONE];

// Порядок уровней в переключателе бонуса локальной игры (по кругу: после
// «божества» — снова нет). Бонусу сетевого лобби доступны не все уровни — см.
// LOBBY_BONUS_ORDER в utils/lobby/lobbyBonus
export const PLAYER_BONUS_ORDER = [
  PLAYER_BONUS_TYPES.NONE,
  PLAYER_BONUS_TYPES.MEDIUM,
  PLAYER_BONUS_TYPES.BIG,
  PLAYER_BONUS_TYPES.LARGE,
  PLAYER_BONUS_TYPES.GREAT,
  PLAYER_BONUS_TYPES.DIVINE,
];

// Известный уровень бонуса — в том числе убранный из выбора SMALL: он есть в
// старых записях и в истории, и их нельзя молча сбрасывать в «нет»
export const checkIfPlayerBonusType = (value) => Object.values(PLAYER_BONUS_TYPES).includes(value);

// Уровень, который можно выбрать сейчас (переключатель локальной игры, харнесс)
export const checkIfSelectablePlayerBonusType = (value) => PLAYER_BONUS_ORDER.includes(value);

// Следующий уровень по кругу заданного порядка; неизвестное значение
// считается «нет»
export const getNextBonusTypeInOrder = (order, bonusType) => {
  const currentIndex = Math.max(order.indexOf(bonusType), 0);
  return order[(currentIndex + 1) % order.length];
};

// Следующий уровень бонуса локальной игры по кругу
export const getNextPlayerBonusType = (bonusType) => getNextBonusTypeInOrder(PLAYER_BONUS_ORDER, bonusType);
